import { forwardRef, HTMLAttributes } from 'react';
import cn from 'classnames';

import hyphenate from 'js/utils/hyphenate';
import OPE from 'js/on-page-editing';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  id?: string;
  level?: number;
  // NOTE: levelOffset is intended to be used by the backend only,
  // as a way to describe the semantic relationships between blocks without having to know what the actual heading levels are.
  levelOffset?: number;
  onPageEditing?: string;
}

const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, children, id, level = 2, levelOffset = 0, onPageEditing, ...rest }, ref) => {
    const headingLevel = Math.min(6, Math.max(1, level + levelOffset));
    const HeadingTag = `h${headingLevel}` as HeadingTag;

    // Only apply hyphenate if children is a string
    const childrenProps = typeof children === 'string' ? hyphenate(children) : { children };

    return (
      <HeadingTag
        className={cn('heading', className)}
        id={id}
        {...childrenProps}
        {...OPE(onPageEditing)}
        {...rest}
        ref={ref}
      />
    );
  }
);
Heading.displayName = 'Heading';

export default Heading;
